// LocalStoreDetail.tsx
import React, { useEffect, useState } from 'react';
import './LocalStoreDetail.scss';
import {
  Drawer,
  Button,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Tabs,
  Tab
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import StorefrontIcon from '@material-ui/icons/Storefront';
import ImageIcon from '@material-ui/icons/Image';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ReceiptLongIcon from '@material-ui/icons/Assignment';
import RemoveIcon from '@material-ui/icons/Remove';
import AddIcon from '@material-ui/icons/Add';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCartOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { http, general, localstorage, AlertService, useNavigationService } from '../fx-core';
import LoginContainer from '../LoginContainer/LoginContainer';
import dummyImage from '../../assets/images/dummy.png';

interface StoreInfo {
  id: number;
  slotId: number;
  storeName: string;
  logoUrl: string | null;
  isLive: boolean;
  slotStart: string;
  slotEnd: string;
  isPickupEnabled: boolean;
  isDeliveryEnabled: boolean;
}

interface RackOption {
  id: number;
  label: string;
}

interface ProductOption {
  id: number;
  rackId: number | null;
  rackLabel: string | null;
  name: string;
  quantity: number;
  price: number | null;
  description: string | null;
  photos: string[];
}

interface CartLine {
  product: ProductOption;
  quantity: number;
}

interface Reservation {
  id: number;
  localStoreBookingId: number;
  orderGroupId?: string;
  expiresAt?: string;
  productName: string;
  quantity: number;
  price: number | null;
  fulfilmentType: string;
  status: string;
  reservedDate: string;
}

const STATUS_LABELS: Record<string, string> = {
  Reserved: 'Reserved',
  PickedUp: 'Picked up',
  Delivered: 'Delivered',
  NoShow: "Didn't collect",
  Expired: 'Expired'
};

const LocalStoreDetail = (props: any) => {
  const { back, navigate } = useNavigationService();
  const dispatch = useDispatch();
  const communityData = useSelector<any, any>(s => s.community);
  const code = props.match?.params?.code || '';
  const urlBookingId = props.match?.params?.bookingId ? parseInt(props.match.params.bookingId) : (props.bookingId || 0);
  const urlSlotId = props.match?.params?.slotId ? parseInt(props.match.params.slotId) : (props.slotId || 0);
  // No bookingId/slotId in the URL means this is the static per-community link
  // (e.g. /:code/localstore) — whichever stall is currently live gets resolved
  // at load time instead of being baked into the link.
  const isStaticLink = !urlBookingId || !urlSlotId;

  const [bookingId, setBookingId] = useState<number>(urlBookingId);
  const [slotId, setSlotId] = useState<number>(urlSlotId);
  const [noStoreLive, setNoStoreLive] = useState(false);
  const userId = localstorage.getItem("userId") ? parseInt(localstorage.getItem("userId")) : 0;

  const [activeTab, setActiveTab] = useState(0); // 0 = Store, 1 = My Orders
  const [loading, setLoading] = useState(true);

  const [storeInfo, setStoreInfo] = useState<StoreInfo | null>(null);
  const [racks, setRacks] = useState<RackOption[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [activeRackId, setActiveRackId] = useState<number | 'all'>('all');

  const [isPickupEnabled, setIsPickupEnabled] = useState(true);
  const [isDeliveryEnabled, setIsDeliveryEnabled] = useState(false);

  const [openLogin, setOpenLogin] = useState(false);

  // Cart
  const [cart, setCart] = useState<CartLine[]>([]);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [cartFulfilment, setCartFulfilment] = useState<'Pickup' | 'Delivery'>('Pickup');
  const [checkingOut, setCheckingOut] = useState(false);

  // My Orders
  const [myReservations, setMyReservations] = useState<Reservation[]>([]);
  const [loadingReservations, setLoadingReservations] = useState(false);
  const [, forceOrdersTick] = useState(0);

  // ── Data loading ─────────────────────────────────────────────────
    const getStoreInfo = () => {
    const communityId = localstorage.getItem("communityId");
    if (!communityId) return Promise.resolve();
    return http.get({ url: `localstore/community/${communityId}/featured` })
      .then((res: any) => {
        const stores: StoreInfo[] = res.data?.stores || [];
        const match = stores.find(s => s.id === bookingId && s.slotId === slotId);
        setStoreInfo(match || null);
        if (match) {
          setIsPickupEnabled(!!match.isPickupEnabled);
          setIsDeliveryEnabled(!!match.isDeliveryEnabled);
          // Keep the cart's default fulfilment option in sync with what this
          // stall actually supports (mirrors the old per-product dialog default).
          setCartFulfilment(match.isPickupEnabled ? 'Pickup' : 'Delivery');
        }
      })
      .catch(() => setStoreInfo(null));
  };

  const getRacks = () => {
    return http.get({ url: `localstore/${bookingId}/racks` })
      .then((res: any) => setRacks(res.data?.racks || []))
      .catch(() => setRacks([]));
  };

  const getProducts = () => {
    return http.get({ url: `localstore/${bookingId}/products` })
      .then((res: any) => {
        // NOTE: unlike other parts of the app, Local Store product photos are
        // already complete CloudFront URLs (returned as `publicUrl` by the
        // presign endpoint and saved as-is — see LocalStoreInventory.tsx's
        // uploadProductImages). They must NOT be run through
        // general.getImageUrl(), which prepends the Azure Blob Storage base
        // URL and is meant for relative paths only. Doing so here previously
        // produced a broken, doubly-prefixed URL (blob-storage-host/…/https://cloudfront-host/…).
        setProducts(res.data?.products || []);
      })
      .catch(() => setProducts([]));
  };

  // Resolves communityId, preferring whatever's already in the Redux store
  // (the normal case — someone browsing the app already went through the
  // usual community bootstrap). For a cold, unauthenticated landing (a fresh
  // QR/WhatsApp click straight into this page, no prior session), falls back
  // to the same bootstrap CustomerDashboard/Stall.tsx use — this dispatches
  // "set-community-details", which is what the app header reads for its
  // community name, and (via communityReducer) also persists communityId/
  // communityCode to localStorage, which navigate()/getStoreInfo() etc. read
  // directly. Skipping this and only setting localStorage communityId (an
  // earlier, incomplete version of this fix) left the header stuck on its
  // generic fallback since Redux was never populated.
  const resolveCommunityId = (): Promise<number> => {
    const normalizedCode = code.trim().toLowerCase();
    const reduxCode = String(communityData?.code || '').trim().toLowerCase();
    const storedCode = String(localstorage.getItem('communityCode') || '').trim().toLowerCase();

    // The route is authoritative. Reuse cached community state only when it
    // belongs to the code in the URL; otherwise a cold link can accidentally
    // query the last community visited in this browser and report no store.
    if (communityData?.id && reduxCode === normalizedCode) {
      return Promise.resolve(communityData.id);
    }
    const stored = localstorage.getItem("communityId");
    if (stored && storedCode === normalizedCode) {
      return Promise.resolve(parseInt(stored));
    }
    if (!code) return Promise.resolve(0);
    return http.post({ url: 'portal/validate/community/code', data: { code } })
      .then((res: any) => {
        const community = res.data?.community;
        if (!community?.id) return 0;
        localstorage.setItem('imagStorageUrl', res.data.imageStorageUrl);
        dispatch({ type: 'set-community-details', payload: community });
        return community.id;
      })
      .catch(() => 0);
  };

  // Finds whichever slot is live right now for this community and points the
  // page at it. Reuses the same "featured" list + isLive flag the carousel
  // already relies on elsewhere, rather than introducing a new lookup.
  const resolveLiveStore = () => {
    return resolveCommunityId().then((communityId) => {
      if (!communityId) {
        setNoStoreLive(true);
        setLoading(false);
        return;
      }
      return http.get({ url: `localstore/community/${communityId}/featured` })
        .then((res: any) => {
          const stores: StoreInfo[] = res.data?.stores || [];
          const live = stores.find(s => s.isLive);
          if (live) {
            setNoStoreLive(false);
            setBookingId(live.id);
            setSlotId(live.slotId);
          } else {
            setNoStoreLive(true);
            setStoreInfo(null);
            setLoading(false);
          }
        })
        .catch(() => { setNoStoreLive(true); setLoading(false); });
    });
  };

  const loadAll = () => {
    if (!bookingId || !slotId) {
      if (isStaticLink) {
        setLoading(true);
        resolveLiveStore();
      } else {
        setLoading(false);
        setStoreInfo(null);
      }
      return;
    }
    setLoading(true);
    Promise.all([getStoreInfo(), getRacks(), getProducts()]).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, slotId]);

  // Static link only: keep checking who's live so the page swaps over on its
  // own if left open across a stall closing and the next one activating —
  // never a stale "closed" screen once someone else has taken over the slot.
  useEffect(() => {
    if (!isStaticLink) return;
    const pollInterval = setInterval(() => resolveLiveStore(), 30000);
    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The cart is scoped to one merchant's live stall — if the static link
  // swaps to a different stall mid-session, stale cart lines would reference
  // another merchant's products. Checkout already guards against this
  // server-side (MIXED_STORES/STORE_CLOSED), but clearing here avoids ever
  // showing that error in the first place.
  useEffect(() => {
    if (isStaticLink) setCart([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const getMyReservations = (silent = false) => {
    if (!userId) return;
    if (!silent) setLoadingReservations(true);
    http.get({ url: `localstore/user/${userId}/reservations` })
      .then((res: any) => {
        const all: Reservation[] = res.data?.reservations || [];
        setMyReservations(all.filter(r => r.localStoreBookingId === bookingId));
      })
      .catch(() => { if (!silent) setMyReservations([]); })
      .finally(() => { if (!silent) setLoadingReservations(false); });
  };

  useEffect(() => {
    if (activeTab === 1) getMyReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Live countdown on Reserved orders, and a quiet background refresh —
  // both only while the My Orders tab is actually open.
  useEffect(() => {
    if (activeTab !== 1) return;
    const tickInterval = setInterval(() => forceOrdersTick(t => t + 1), 1000);
    return () => clearInterval(tickInterval);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 1) return;
    const pollInterval = setInterval(() => getMyReservations(true), 30000);
    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ── Rack filtering (client-side) ──────────────────────────────────
  // Racks come back from the backend in whatever order they were created —
  // often alphabetical ("Front", "Last", "Mid"), which reads wrong physically.
  // Sort by shelf front-to-back position instead; unrecognized labels keep
  // their original relative order, after these.
  const RACK_POSITION_ORDER = ['front', 'mid', 'middle', 'last', 'back'];
  const rackPositionRank = (label: string) => {
    const normalized = label.trim().toLowerCase();
    const idx = RACK_POSITION_ORDER.findIndex(keyword => normalized.includes(keyword));
    return idx === -1 ? RACK_POSITION_ORDER.length : idx;
  };
  const sortedRacks = [...racks].sort((a, b) => rackPositionRank(a.label) - rackPositionRank(b.label));

  const visibleProducts = activeRackId === 'all'
    ? products.filter(p => p.rackId !== null) // buyer-facing view only shows racked items
    : products.filter(p => p.rackId === activeRackId);

  // ── My Orders grouping (mirrors the merchant order-card grouping) ──
  const isValidDate = (value?: string) => !!value && !Number.isNaN(new Date(value).getTime());

  const formatCountdown = (expiresAt?: string) => {
    if (!isValidDate(expiresAt)) return null;
    const diffMs = new Date(expiresAt as string).getTime() - Date.now();
    if (diffMs <= 0) return 'Expired';
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  const groupedMyOrders = React.useMemo(() => {
    const groups = new Map<string, Reservation[]>();
    myReservations.forEach(r => {
      const groupId = r.orderGroupId?.trim() || `reservation-${r.id}`;
      const lines = groups.get(groupId) || [];
      lines.push(r);
      groups.set(groupId, lines);
    });

    const result = Array.from(groups.entries()).map(([groupId, lines]) => {
      const firstLine = lines[0];
      const expiresAt = lines.map(l => l.expiresAt).find(isValidDate);
      const isPastExpiry = firstLine.status === 'Reserved' && firstLine.fulfilmentType !== 'Delivery'
        && lines.every(l => l.status === 'Reserved') && !!expiresAt
        && new Date(expiresAt as string).getTime() <= Date.now();
      return {
        groupId,
        lines,
        // orderGroupId (a GUID) is only needed for grouping — shown to the
        // buyer as the numeric id of its earliest reservation line instead.
        orderLabel: String(Math.min(...lines.map(l => l.id))),
        reservedDate: lines.reduce((latest, l) => l.reservedDate > latest ? l.reservedDate : latest, firstLine.reservedDate),
        expiresAt,
        status: isPastExpiry ? 'Expired' : firstLine.status,
        fulfilmentType: firstLine.fulfilmentType,
        totalAmount: lines.reduce((sum, l) => sum + (l.price != null ? l.price * l.quantity : 0), 0),
        hasUnpricedLine: lines.some(l => l.price == null),
      };
    });

    result.sort((a, b) => new Date(b.reservedDate).getTime() - new Date(a.reservedDate).getTime());
    return result;
  }, [myReservations]);

  // ── Cart flow ────────────────────────────────────────────────────
  // Card's ADD control is bound straight to the cart (Amazon/Flipkart-style
  // "ADD" pill that morphs into a − qty + stepper) — no separate pre-add
  // quantity picker, so there's only one control per product, ever.
  const getCartQuantity = (productId: number) => cart.find(l => l.product.id === productId)?.quantity ?? 0;

  const incrementCartItem = (product: ProductOption) => {
    if (!userId) {
      AlertService.showMsg({ message: "Please login to add items to your cart" });
      setOpenLogin(true);
      return;
    }
    setCart(prev => {
      const existing = prev.find(l => l.product.id === product.id);
      if (existing) {
        if (existing.quantity >= product.quantity) return prev;
        return prev.map(l => l.product.id === product.id ? { ...l, quantity: l.quantity + 1 } : l);
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const decrementCartItem = (productId: number) => {
    setCart(prev => prev
      .map(l => l.product.id === productId ? { ...l, quantity: l.quantity - 1 } : l)
      .filter(l => l.quantity > 0)
    );
  };

  const updateCartQuantity = (productId: number, quantity: number) => {
    setCart(prev => prev
      .map(l => l.product.id === productId ? { ...l, quantity: Math.max(1, Math.min(quantity, l.product.quantity)) } : l)
    );
  };

  const removeFromCart = (productId: number) => {
    setCart(prev => prev.filter(l => l.product.id !== productId));
  };

  const cartTotal = cart.reduce((sum, l) => sum + (l.product.price ?? 0) * l.quantity, 0);
  const cartCount = cart.reduce((sum, l) => sum + l.quantity, 0);

  const submitCheckout = () => {
    if (cart.length === 0) return;
    setCheckingOut(true);
    http.post({
      url: `localstore/cart/checkout`,
      data: {
        // The server assigns the single orderGroupId shared by every line in
        // this checkout — nothing to pass from here.
        items: cart.map(l => ({ productId: l.product.id, quantity: l.quantity })),
        fulfilmentType: cartFulfilment
      }
    })
      .then((res: any) => {
        if (!res.data?.success) {
          const err = res.data?.error;
          const msg = err === 'OUT_OF_STOCK'
            ? `${res.data?.failedProductName ?? 'One item'} just sold out — please update your cart.`
            : err === 'MIXED_STORES'
            ? 'Your cart has items from more than one stall — please check out one stall at a time.'
            : "Could not complete checkout";
          AlertService.showMsg({ message: msg });
          return;
        }
        const expiresAt = res.data?.expiresAt;
        if (cartFulfilment === 'Pickup' && isValidDate(expiresAt)) {
          const countdown = formatCountdown(expiresAt);
          AlertService.showSuccessMsg(`Order placed! Please collect it before it expires (${countdown} left).`);
        } else {
          AlertService.showSuccessMsg("Order placed! The merchant will have it ready for you.");
        }
        setCart([]);
        setCartDrawerOpen(false);
        getProducts();
        setActiveTab(1);
        getMyReservations();
      })
      .catch(() => AlertService.showMsg({ message: "Could not complete checkout" }))
      .finally(() => setCheckingOut(false));
  };

  const onLoginCallBack = () => {
    setOpenLogin(false);
  };

  // NOTE: every early-return state below must stay wrapped in the same
  // .localstore-detail-ui div as the main render — .lsd-loading-screen /
  // .lsd-empty-screen are nested SCSS selectors scoped under that class
  // (LocalStoreDetail.scss), which also supplies the padding-top that clears
  // the app's fixed header. Render them bare and the content sits unstyled
  // underneath that fixed header — technically rendered, but invisible.
  if (loading) {
    return (
      <div className="localstore-detail-ui">
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" className="lsd-loading-screen">
          <CircularProgress size={26} thickness={4} />
          <Typography className="lsd-loading-text">Loading store…</Typography>
        </Box>
      </div>
    );
  }

  if (noStoreLive) {
    return (
      <div className="localstore-detail-ui">
        <Box className="lsd-empty-screen">
          <StorefrontIcon className="lsd-empty-icon" />
          <Typography className="lsd-empty-title">No stall open right now</Typography>
          <Typography className="lsd-empty-sub">
            This spot is used by different local sellers throughout the week — check back later.
          </Typography>
          <Button className="lsd-back-link-btn" onClick={() => back()}>Go back</Button>
        </Box>
      </div>
    );
  }

  if (!storeInfo) {
    return (
      <div className="localstore-detail-ui">
        <Box className="lsd-empty-screen">
          <StorefrontIcon className="lsd-empty-icon" />
          <Typography className="lsd-empty-title">Store not found</Typography>
          <Typography className="lsd-empty-sub">This stall may have already closed.</Typography>
          <Button className="lsd-back-link-btn" onClick={() => back()}>Go back</Button>
        </Box>
      </div>
    );
  }

  return (
    <div className={`localstore-detail-ui${cartCount > 0 ? ' lsd-has-float-bar' : ''}`}>
      {/* ── Simplified header — logo + name + cart, no reviews/contact/QR ─── */}
      <div className="lsd-header">
        <button className="lsd-back-btn" onClick={() => back()} aria-label="Go back"><ArrowBackIcon /></button>

        <div className="lsd-header-logo">
          {storeInfo.logoUrl ? (
            <img src={storeInfo.logoUrl} alt={storeInfo.storeName} />
          ) : (
            <img src={dummyImage} alt="Store" />
          )}
        </div>

        <div className="lsd-header-info">
          <span className="lsd-store-name">
            {storeInfo.storeName || 'Local Store'}
            {storeInfo.isLive ? (
              <span className="lsd-live-badge"><span className="lsd-live-dot" />Open now</span>
            ) : (
              <span className="lsd-closed-badge">Closed</span>
            )}
          </span>
          <span className="lsd-store-sub">Reserve items — pay the merchant directly, no online payment here</span>
        </div>

        <IconButton className="lsd-cart-btn" onClick={() => setCartDrawerOpen(true)} aria-label="View cart">
          <ShoppingCartIcon />
          {cartCount > 0 && <span className="lsd-cart-badge">{cartCount}</span>}
        </IconButton>
      </div>

      <div className="lsd-tabs-wrap">
        <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)} indicatorColor="primary" textColor="primary">
          <Tab label="Store" />
          <Tab
            label={
              <span className="lsd-tab-label">
                <ReceiptLongIcon fontSize="small" />
                My Orders
              </span>
            }
          />
        </Tabs>
      </div>

      {/* ── STORE TAB ────────────────────────────────────────────────── */}
      {activeTab === 0 && (
        <div className="lsd-content">
          {!storeInfo.isLive && (
            <div className="lsd-closed-banner">
              This stall isn't open yet — check back once it goes live to reserve items.
            </div>
          )}

          {racks.length > 0 && (
            <div className="lsd-capsule-row">
              <button
                className={`lsd-capsule${activeRackId === 'all' ? ' lsd-capsule-active' : ''}`}
                onClick={() => setActiveRackId('all')}
              >
                All
              </button>
              {sortedRacks.map((rack) => (
                <button
                  key={rack.id}
                  className={`lsd-capsule${activeRackId === rack.id ? ' lsd-capsule-active' : ''}`}
                  onClick={() => setActiveRackId(rack.id)}
                >
                  {rack.label}
                </button>
              ))}
            </div>
          )}

          {visibleProducts.length === 0 && (
            <div className="lsd-empty-state">
              <StorefrontIcon className="lsd-empty-icon" />
              <Typography className="lsd-empty-title">Nothing on display here yet</Typography>
              <Typography className="lsd-empty-sub">Check back soon, or try a different rack.</Typography>
            </div>
          )}

          <div className="lsd-product-grid">
            {visibleProducts.map((product) => {
              const isSoldOut = product.quantity <= 0;
              return (
                <div
                  className={`lsd-product-card${isSoldOut ? ' lsd-product-card-sold-out' : ''}`}
                  key={product.id}
                >
                  <div className="lsd-product-thumb-wrap">
                    {product.photos && product.photos[0] ? (
                      <img src={product.photos[0]} alt={product.name} className="lsd-product-thumb" />
                    ) : (
                      <div className="lsd-product-thumb-placeholder"><ImageIcon /></div>
                    )}
                    {product.rackLabel && <span className="lsd-rack-tag">{product.rackLabel}</span>}
                    {isSoldOut && (
                      <div className="lsd-sold-out-overlay">
                        <span className="lsd-sold-out-pill">Sold out</span>
                      </div>
                    )}
                  </div>
                  <div className="lsd-product-body">
                    <div className="lsd-product-name">{product.name}</div>
                    {product.description && <div className="lsd-product-desc">{product.description}</div>}
                    {!isSoldOut && (
                      <div className={`lsd-product-stock${product.quantity <= 5 ? ' lsd-product-stock-low' : ''}`}>
                        {product.quantity <= 5 ? `Only ${product.quantity} left` : `${product.quantity} available`}
                      </div>
                    )}
                    <div className="lsd-price-add-row">
                      <div className="lsd-product-price">
                        {product.price != null ? `₹${product.price}` : 'Ask price'}
                      </div>
                      {isSoldOut ? (
                        <span className="lsd-sold-out-label">Sold out</span>
                      ) : (() => {
                        const cartQty = getCartQuantity(product.id);
                        return cartQty === 0 ? (
                          <button
                            type="button"
                            className="lsd-add-btn"
                            disabled={!storeInfo.isLive}
                            onClick={() => incrementCartItem(product)}
                          >
                            <ShoppingCartIcon fontSize="small" />
                            ADD
                          </button>
                        ) : (
                          <div className="lsd-qty-stepper lsd-card-qty-stepper">
                            <IconButton
                              size="small"
                              onClick={() => decrementCartItem(product.id)}
                              disabled={!storeInfo.isLive}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <span className="lsd-qty-value">{cartQty}</span>
                            <IconButton
                              size="small"
                              onClick={() => incrementCartItem(product)}
                              disabled={!storeInfo.isLive || cartQty >= product.quantity}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── MY ORDERS TAB ────────────────────────────────────────────── */}
      {activeTab === 1 && (
        <div className="lsd-content">
          {loadingReservations && (
            <Box display="flex" justifyContent="center" py={5}><CircularProgress size={24} /></Box>
          )}

          {!loadingReservations && groupedMyOrders.length === 0 && (
            <div className="lsd-empty-state">
              <ReceiptLongIcon className="lsd-empty-icon" />
              <Typography className="lsd-empty-title">No orders yet</Typography>
              <Typography className="lsd-empty-sub">Items you reserve from this stall will show up here.</Typography>
            </div>
          )}

          {!loadingReservations && groupedMyOrders.map((order) => (
            <div className="lsd-order-row" key={order.groupId}>
              <div className="lsd-order-row-header">
                <span className="lsd-order-id-badge">Order #{order.orderLabel}</span>
                <span className={`lsd-order-status lsd-order-status-${order.status.toLowerCase()}`}>
                  {STATUS_LABELS[order.status] || order.status}
                </span>
              </div>

              <div className="lsd-order-products">
                {order.lines.map(line => (
                  <div className="lsd-order-product-line" key={line.id}>
                    <span>{line.productName}<span className="lsd-order-product-qty">× {line.quantity}</span></span>
                    <span className="lsd-order-product-price">
                      {line.price != null ? `₹${(line.price * line.quantity).toLocaleString('en-IN')}` : 'Ask price'}
                    </span>
                  </div>
                ))}
              </div>

              {order.totalAmount > 0 && (
                <div className="lsd-order-total-row">
                  <span>Total{order.hasUnpricedLine ? ' (excl. ask-price items)' : ''}</span>
                  <span className="lsd-order-total-value">₹{order.totalAmount.toLocaleString('en-IN')}</span>
                </div>
              )}

              <div className="lsd-order-meta">
                {order.fulfilmentType} · {new Date(order.reservedDate).toLocaleDateString()}
              </div>

              {order.status === 'Reserved' && order.fulfilmentType !== 'Delivery' && order.expiresAt && (
                <div className="lsd-order-collect-note">
                  <InfoOutlinedIcon fontSize="small" />
                  <span>Please collect before it expires — <b>{formatCountdown(order.expiresAt)}</b> left</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Swiggy-style floating "Go to cart" bar — only once something's in
          the cart, and hidden while the drawer it opens is already open. */}
      {cartCount > 0 && !cartDrawerOpen && (
        <button type="button" className="lsd-go-to-cart-bar" onClick={() => setCartDrawerOpen(true)}>
          <span className="lsd-go-to-cart-count">{cartCount} item{cartCount !== 1 ? 's' : ''} added</span>
          <span className="lsd-go-to-cart-label">
            View cart <ShoppingCartIcon fontSize="small" />
          </span>
        </button>
      )}

      {/* Cart drawer — replaces the old single-product reserve dialog */}
      <Drawer anchor="right" open={cartDrawerOpen} onClose={() => setCartDrawerOpen(false)}>
        <div className="lsd-cart-drawer">
          <div className="lsd-cart-header">
            <Typography className="lsd-cart-title">Your Cart</Typography>
            <IconButton size="small" onClick={() => setCartDrawerOpen(false)}><CloseIcon fontSize="small" /></IconButton>
          </div>

          {cart.length === 0 ? (
            <div className="lsd-empty-state">
              <ShoppingCartIcon className="lsd-empty-icon" />
              <Typography className="lsd-empty-title">Your cart is empty</Typography>
            </div>
          ) : (
            <>
              <div className="lsd-cart-lines">
                {cart.map(line => (
                  <div className="lsd-cart-line" key={line.product.id}>
                    {line.product.photos?.[0]
                      ? <img src={line.product.photos[0]} alt={line.product.name} className="lsd-cart-line-thumb" />
                      : <div className="lsd-cart-line-thumb-placeholder"><ImageIcon fontSize="small" /></div>}
                    <div className="lsd-cart-line-info">
                      <div className="lsd-cart-line-name">{line.product.name}</div>
                      <div className="lsd-cart-line-price">
                        {line.product.price != null ? `₹${line.product.price} each` : 'Price on request'}
                      </div>
                      <div className="lsd-qty-stepper">
                        <IconButton size="small" onClick={() => updateCartQuantity(line.product.id, line.quantity - 1)} disabled={line.quantity <= 1}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <span className="lsd-qty-value">{line.quantity}</span>
                        <IconButton size="small" onClick={() => updateCartQuantity(line.product.id, line.quantity + 1)} disabled={line.quantity >= line.product.quantity}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                    <IconButton size="small" className="lsd-cart-line-remove" onClick={() => removeFromCart(line.product.id)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
              </div>

              <div className="lsd-reserve-section">
                <Typography className="lsd-reserve-section-label">Fulfilment</Typography>
                <div className="lsd-fulfilment-options">
                  {isPickupEnabled && (
                    <button type="button" className={`lsd-fulfilment-card${cartFulfilment === 'Pickup' ? ' lsd-fulfilment-card-active' : ''}`} onClick={() => setCartFulfilment('Pickup')}>
                      <StorefrontIcon fontSize="small" /><span>Pickup at stall</span>
                    </button>
                  )}
                  {isDeliveryEnabled && (
                    <button type="button" className={`lsd-fulfilment-card${cartFulfilment === 'Delivery' ? ' lsd-fulfilment-card-active' : ''}`} onClick={() => setCartFulfilment('Delivery')}>
                      <LocalShippingIcon fontSize="small" /><span>Delivery</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="lsd-reserve-total-row">
                <span>Estimated total</span>
                <span className="lsd-reserve-total-value">₹{cartTotal.toLocaleString('en-IN')}</span>
              </div>

              <div className="lsd-reserve-info-note">
                <InfoOutlinedIcon fontSize="small" />
                <span>This reserves your items with the merchant — no payment happens here. Pay them directly at pickup or delivery.</span>
              </div>

              <Button variant="contained" className="lsd-reserve-confirm-btn" fullWidth onClick={submitCheckout} disabled={checkingOut}>
                {checkingOut ? <CircularProgress size={18} /> : `Place order · ₹${cartTotal.toLocaleString('en-IN')}`}
              </Button>
            </>
          )}
        </div>
      </Drawer>

      {/* Login drawer, same pattern as StallHeader */}
      <Drawer anchor="right" open={openLogin} onClose={() => setOpenLogin(false)}>
        <LoginContainer handleLoginClose={() => setOpenLogin(false)} onLoginCallBack={onLoginCallBack} />
      </Drawer>
    </div>
  );
};

export default LocalStoreDetail;
